import React, { useEffect, useState, useCallback } from 'react';
import { Page, Header, Content, Progress } from '@backstage/core-components';
import {
  Box,
  Typography,
  Link,
  Chip,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import CodeIcon from '@material-ui/icons/Code';

const useStyles = makeStyles(theme => ({
  content: {
    maxWidth: 1200,
    margin: '0 auto',
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionIcon: {
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  listPanel: {
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  listItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  listItem: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.default,
    transition: 'background 0.2s',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  listItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  listItemTitle: {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  listItemMeta: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  statusChip: {
    height: 20,
    fontSize: '0.75rem',
  },
  stateOpen: {
    background: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  stateClosed: {
    background: theme.palette.grey[500],
    color: theme.palette.common.white,
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  stats: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

interface PullRequest {
  id: number;
  number: number;
  title: string;
  url: string;
  state: string;
  repo: string;
  updatedAt: string;
  createdAt: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const PullRequestsPage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [pullRequests, setPullRequests] = useState<PullRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [githubUsername, setGithubUsername] = useState<string | null>(null);

  const fetchGithubUsername = useCallback(async () => {
    try {
      const profile = await identityApi.getProfileInfo();
      const userEmail = profile.email;

      if (userEmail) {
        const usernameResponse = await fetch(
          `${backendUrl}/api/github/username-from-email?email=${encodeURIComponent(
            userEmail,
          )}`,
        );

        if (usernameResponse.ok) {
          const usernameData = await usernameResponse.json();
          if (usernameData.found) {
            setGithubUsername(usernameData.username);
            return usernameData.username;
          }
        }
      }
    } catch (err) {
      console.error('Error fetching GitHub username:', err);
    }
    return null;
  }, [backendUrl, identityApi]);

  const fetchPullRequests = useCallback(
    async (username: string) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          state: 'open',
          limit: '100', // Fetch up to 100 PRs
          searchBy: 'username',
        });

        const response = await fetch(
          `${backendUrl}/api/github/users/${encodeURIComponent(
            username,
          )}/pulls?${params}`,
        );

        if (!response.ok) {
          throw new Error(`GitHub request failed: ${response.status}`);
        }

        const data = await response.json();
        setPullRequests(data.items || []);
      } catch (err) {
        console.error('Error fetching pull requests:', err);
        setError('Failed to load pull requests. Please try again later.');
      } finally {
        setLoading(false);
      }
    },
    [backendUrl],
  );

  useEffect(() => {
    const init = async () => {
      const username = await fetchGithubUsername();
      if (username) {
        await fetchPullRequests(username);
      } else {
        setLoading(false);
        setError('Unable to determine GitHub username.');
      }
    };

    init();
  }, [fetchGithubUsername, fetchPullRequests]);

  return (
    <Page themeId="home">
      <Header
        title="Mis Pull Requests"
        subtitle="Todos tus pull requests abiertos en los repositorios"
      />
      <Content>
        <Box className={classes.content}>
          {githubUsername && (
            <Box className={classes.stats}>
              <Box className={classes.statItem}>
                <Typography className={classes.statValue}>
                  {pullRequests.length}
                </Typography>
                <Typography className={classes.statLabel}>
                  Pull Requests Abiertos
                </Typography>
              </Box>
              <Box className={classes.statItem}>
                <Typography className={classes.statValue}>
                  @{githubUsername}
                </Typography>
                <Typography className={classes.statLabel}>
                  Usuario de GitHub
                </Typography>
              </Box>
            </Box>
          )}

          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Box className={classes.sectionTitleWrap}>
                <CodeIcon className={classes.sectionIcon} />
                <Typography className={classes.sectionTitle}>
                  Pull Requests Abiertos
                </Typography>
              </Box>
            </Box>
            <Box className={classes.listPanel}>
              {loading ? (
                <Box className={classes.loadingBox}>
                  <CircularProgress size={40} />
                  <Typography>Cargando pull requests...</Typography>
                </Box>
              ) : error ? (
                <Box className={classes.emptyState}>{error}</Box>
              ) : pullRequests.length === 0 ? (
                <Box className={classes.emptyState}>
                  No se encontraron pull requests abiertos.
                </Box>
              ) : (
                <Box className={classes.listItems}>
                  {pullRequests.map(pr => (
                    <Box key={pr.id} className={classes.listItem}>
                      <Box className={classes.listItemHeader}>
                        <Link
                          href={pr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.listItemTitle}
                        >
                          {pr.repo}#{pr.number}: {pr.title}
                        </Link>
                        <Chip
                          size="small"
                          label={pr.state}
                          className={`${classes.statusChip} ${
                            pr.state === 'open'
                              ? classes.stateOpen
                              : classes.stateClosed
                          }`}
                        />
                      </Box>
                      <Typography className={classes.listItemMeta}>
                        Creado {formatDate(pr.createdAt)} • Actualizado{' '}
                        {formatDate(pr.updatedAt)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
